import React, { useEffect, useRef, useState } from "react";

/**
 * VesselMap — live vessel position from AIS on a Leaflet + OpenStreetMap map.
 *
 * Leaflet is loaded from CDN on demand (no npm dependency). Pass a `fetcher` that
 * resolves to { mmsi, vessel_name, pol_name, pod_name, position:{lat,lon,course,speed,
 * heading,...}|null, age_minutes }. The map polls every `intervalMs` (default 45s).
 *
 * Works for both the staff app and the customer portal — just pass the right fetcher.
 */
const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_JS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";

function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = LEAFLET_CSS;
    document.head.appendChild(link);
  }
  return new Promise((resolve, reject) => {
    if (window.__leafletLoading) {
      window.__leafletLoading.then(resolve).catch(reject);
      return;
    }
    window.__leafletLoading = new Promise((res, rej) => {
      const s = document.createElement("script");
      s.src = LEAFLET_JS;
      s.async = true;
      s.onload = () => res(window.L);
      s.onerror = rej;
      document.body.appendChild(s);
    });
    window.__leafletLoading.then(resolve).catch(reject);
  });
}

const fmtAge = (m) => {
  if (m == null) return "—";
  if (m < 60) return `${m} min ago`;
  if (m < 1440) return `${Math.round(m / 60)} h ago`;
  return `${Math.round(m / 1440)} d ago`;
};

export default function VesselMap({ fetcher, height = 320, intervalMs = 45000, theme = "dark" }) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [ready, setReady] = useState(false);

  // Init the map once Leaflet is available.
  useEffect(() => {
    let cancelled = false;
    loadLeaflet()
      .then((L) => {
        if (cancelled || !mapEl.current || mapRef.current) return;
        const map = L.map(mapEl.current, { zoomControl: true, attributionControl: true }).setView([20, 0], 2);
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 12, attribution: "&copy; OpenStreetMap",
        }).addTo(map);
        mapRef.current = map;
        setReady(true);
        // The card often finishes layout a beat after init — without this the tiles
        // render grey/blank until the map learns its real pixel size.
        setTimeout(() => { try { map.invalidateSize(); } catch (_) {} }, 250);
        setTimeout(() => { try { map.invalidateSize(); } catch (_) {} }, 1200);
      })
      .catch(() => setErr("Map failed to load (network)."));
    return () => {
      cancelled = true;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
  }, []);

  // Poll the position.
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const d = await fetcher();
        if (!stop) { setData(d); setErr(null); }
      } catch (e) {
        if (!stop) setErr("Couldn't load vessel position.");
      }
    };
    tick();
    const id = setInterval(tick, intervalMs);
    return () => { stop = true; clearInterval(id); };
  }, [fetcher, intervalMs]);

  // Render the marker when we have both a map and a fix.
  useEffect(() => {
    const L = window.L;
    const map = mapRef.current;
    if (!L || !map || !ready) return;
    const pos = data && data.position;
    if (!pos || pos.lat == null || pos.lon == null) return;

    const heading = pos.heading != null && pos.heading < 360 ? pos.heading : (pos.course || 0);
    const html = `<div style="transform:rotate(${heading}deg);font-size:22px;line-height:1">▲</div>`;
    const icon = L.divIcon({ className: "vessel-mkr", html, iconSize: [22, 22], iconAnchor: [11, 11] });

    if (!markerRef.current) {
      markerRef.current = L.marker([pos.lat, pos.lon], { icon }).addTo(map);
      map.setView([pos.lat, pos.lon], 5);
      setTimeout(() => { try { map.invalidateSize(); } catch (_) {} }, 100);
    } else {
      markerRef.current.setLatLng([pos.lat, pos.lon]);
      markerRef.current.setIcon(icon);
    }
    markerRef.current.bindPopup(
      `<b>${(data.vessel_name || "Vessel")}</b><br/>` +
      `${pos.speed != null ? pos.speed + " kn · " : ""}${heading}&deg;<br/>` +
      `<small>${fmtAge(data.age_minutes)}</small>`
    );
  }, [data, ready]);

  const t = theme === "dark"
    ? { card: "bg-[#0c0d12] border-[#272A35] text-gray-300", sub: "text-gray-500" }
    : { card: "bg-white border-slate-200 text-slate-700", sub: "text-slate-400" };

  const noMmsi = data && !data.mmsi;
  const noFix = data && data.mmsi && !data.position;

  return (
    <div className={`rounded-md border overflow-hidden ${t.card}`}>
      <div className="flex items-center justify-between px-3 py-2 text-xs">
        <span className="font-mono uppercase tracking-wider">Live Vessel Position</span>
        <span className={t.sub}>
          {data?.vessel_name ? data.vessel_name : ""}{data?.position ? ` · ${fmtAge(data.age_minutes)}` : ""}
        </span>
      </div>
      <div ref={mapEl} style={{ height, width: "100%" }} />
      {(err || noMmsi || noFix) && (
        <div className={`px-3 py-2 text-[11px] ${t.sub}`}>
          {err ? err
            : noMmsi ? "Add the vessel's MMSI (Routing section) to show it on the map."
            : "Awaiting first AIS signal — mid-ocean vessels can take a while to report."}
        </div>
      )}
    </div>
  );
}
